import React, { useState } from "react";
import {
  Button,
  Dialog,
  Card,
  CardBody,
  CardFooter,
  Typography,
  Input,
} from "@material-tailwind/react";
import { useHttp } from "@/hooks/useFetch";
import { useGlobalData } from "../Layout/GlobalContext";
import { ConfrimModal } from "../Layout/Confrim";
import { XCircleIcon } from "@heroicons/react/24/outline";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (v: string) => EMAIL_RE.test(v.trim());

/**
 * 邮箱注册 Dialog
 *
 * 流程：
 * 1. 输入邮箱 + 设置密码 + 确认密码
 * 2. 点"发送验证码" → 后端 IP 限流 + 邮箱 60s 限频 + 邮箱未注册校验 → 发邮件
 * 3. 输入邮箱验证码 → 点"注册"
 * 4. 后端 /app/user/login/register-email：校验邮箱码 + 写入用户 + 颁发 token
 */
export function RegisterDialog() {
  const { Http } = useHttp();
  const {
    registerModal,
    setRegisterModal,
    setLoginModal,
    alert,
    registerByEmail,
  } = useGlobalData();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [submit, setSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // mock 模式下后端会把验证码塞回 Response.mockCode，弹窗展示便于联调
  const [mockCode, setMockCode] = useState("");
  const [mockOpen, setMockOpen] = useState(false);

  const [countdown, setCountdown] = useState(0);
  const countdownRef = React.useRef(0);

  const handleClose = () => {
    setRegisterModal(false);
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setPassword2("");
    setEmailCode("");
    setSubmit(false);
  };

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = 60;
    const timer = window.setInterval(() => {
      countdownRef.current -= 1;
      setCountdown(countdownRef.current);
      if (countdownRef.current <= 0) {
        window.clearInterval(timer);
      }
    }, 1000);
  };

  const handleSendCode = async () => {
    setSubmit(true);
    if (!isValidEmail(email)) {
      alert({ color: "red", message: "请输入有效的邮箱地址" });
      return;
    }
    setSending(true);
    try {
      const data: any = await Http.post("/app/user/login/emailCode", {
        email: email.trim(),
      });
      if (data?.mockCode) {
        setMockCode(String(data.mockCode));
        setMockOpen(true);
      } else {
        alert({ color: "green", message: "验证码已发送到邮箱，请注意查收" });
      }
      startCountdown();
    } catch (error) {
      // Http 拦截器已统一弹错（如 60s 限频 / IP 超限），此处无须额外处理
    } finally {
      setSending(false);
    }
  };

  const handleRegister = async () => {
    setSubmit(true);
    if (!isValidEmail(email)) {
      alert({ color: "red", message: "请输入有效的邮箱地址" });
      return;
    }
    if (!password || password.length < 6) {
      alert({ color: "red", message: "密码至少 6 位" });
      return;
    }
    if (password !== password2) {
      alert({ color: "red", message: "两次密码不一致" });
      return;
    }
    if (!emailCode) {
      alert({ color: "red", message: "请输入邮箱验证码" });
      return;
    }
    setLoading(true);
    try {
      await registerByEmail(email.trim(), password, emailCode.trim());
      alert({ color: "green", message: "注册成功" });
      setRegisterModal(false);
      resetForm();
    } catch (error) {
      // registerByEmail 内部已 alert，这里兜底
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        size="md"
        open={registerModal}
        handler={() => setRegisterModal(true)}
        className="bg-transparent shadow-none"
      >
        <Card className="mx-auto w-full md:max-w-[24rem]">
          <CardBody className="flex flex-col gap-4">
            <XCircleIcon
              className="absolute w-7 right-5 top-5 cursor-pointer"
              onClick={handleClose}
            />
            <Typography variant="h4" color="blue-gray">
              邮箱注册
            </Typography>

            <Typography className="-mb-2" variant="h6">
              邮箱
            </Typography>
            <Input
              error={submit && !isValidEmail(email)}
              type="email"
              value={email}
              onChange={({ target }) => setEmail(target.value)}
              crossOrigin=""
              label="邮箱地址"
              size="lg"
              autoComplete="email"
            />

            <Typography className="-mb-2" variant="h6">
              密码
            </Typography>
            <Input
              error={submit && (!password || password.length < 6)}
              type="password"
              value={password}
              onChange={({ target }) => setPassword(target.value)}
              crossOrigin=""
              label="至少 6 位"
              size="lg"
              autoComplete="new-password"
            />
            <Input
              error={submit && password !== password2}
              type="password"
              value={password2}
              onChange={({ target }) => setPassword2(target.value)}
              crossOrigin=""
              label="确认密码"
              size="lg"
              autoComplete="new-password"
            />

            <Typography className="-mb-2" variant="h6">
              邮箱验证码
            </Typography>
            <div className="relative flex w-full">
              <Input
                error={submit && !emailCode}
                type="text"
                maxLength={6}
                value={emailCode}
                onChange={({ target }) => setEmailCode(target.value)}
                crossOrigin=""
                label="6 位验证码"
                size="lg"
                className="pr-32"
                containerProps={{ className: "min-w-0" }}
              />
              <Button
                onClick={handleSendCode}
                size="sm"
                disabled={!!countdown || sending}
                className="!absolute right-1.5 top-1.5 rounded"
              >
                {countdown
                  ? `${countdown}s`
                  : sending
                  ? "发送中..."
                  : "发送验证码"}
              </Button>
            </div>
          </CardBody>
          <CardFooter className="pt-0">
            <Button
              variant="gradient"
              onClick={handleRegister}
              fullWidth
              loading={loading}
              disabled={loading}
            >
              注册
            </Button>
            <Typography variant="small" className="mt-3 flex justify-center">
              已经有账号？
              <Typography
                as="a"
                href="#login"
                variant="small"
                color="blue-gray"
                className="ml-1 font-bold cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                  setLoginModal(true);
                }}
              >
                去登录
              </Typography>
            </Typography>
          </CardFooter>
        </Card>
      </Dialog>

      <ConfrimModal
        header={
          <div className="text-xl font-bold text-center w-full mt-5">
            邮箱验证码（开发模式）
          </div>
        }
        content={
          <div className="text-3xl font-bold text-center w-full tracking-widest">
            {mockCode}
          </div>
        }
        open={mockOpen}
        onConfirm={() => setMockOpen(false)}
        onCancel={() => setMockOpen(false)}
      />
    </>
  );
}
